import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listingsApi, bookingsApi, uploadApi, categoriesApi, getPhotoUrl, FALLBACK_LISTING_IMAGE } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { AMENITIES, BOOKING_STATUS } from '../utils/constants';
import { toast } from 'sonner';
import { 
  House, 
  ListDashes, 
  CalendarBlank, 
  ChatsCircle, 
  CurrencyDollar, 
  Star, 
  Gear,
  Plus,
  X,
  Upload,
  CaretRight,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  Eye,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';
import AvailabilityCalendar from '../components/AvailabilityCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab Components
const TABS = [
  { id: 'overview', label: 'Overview', icon: House },
  { id: 'listings', label: 'My Listings', icon: ListDashes },
  { id: 'bookings', label: 'Bookings', icon: CalendarBlank },
  { id: 'calendar', label: 'Calendar', icon: CalendarBlank },
  { id: 'messages', label: 'Messages', icon: ChatsCircle },
  { id: 'earnings', label: 'Earnings', icon: CurrencyDollar },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'settings', label: 'Settings', icon: Gear },
];

export default function HostDashboard() {
  const navigate = useNavigate();
  const { user, token, activeMode } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Dynamic categories from admin
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Form state for creating listings
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price_per_hour: '',
    location_city: '',
    location_state: '',
    location_zip: '',
    location_neighborhood: '',
    address_full: '',
    max_guests: 1,
    amenities: [],
    rules: [],
    photos: [],
    terms_accepted: false,
  });
  const [newRule, setNewRule] = useState('');

  useEffect(() => {
    if (!user?.is_host && user?.role !== 'admin') {
      navigate('/become-host');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadListings(),
        loadBookings(),
        loadReviews(),
        loadCategories()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/host/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadListings = async () => {
    try {
      const response = await listingsApi.getHostListings(token);
      setListings(response.data);
    } catch (error) {
      console.error('Failed to load listings:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/host/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to load bookings:', error);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/host/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    
    try {
      const uploadPromises = files.map(file => uploadApi.upload(file, token));
      const results = await Promise.all(uploadPromises);
      const paths = results.map(r => r.data.path);
      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...paths] }));
      toast.success('Photos uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    
    // Validate photos
    if (formData.photos.length === 0) {
      toast.error('Please upload at least one photo of your space');
      return;
    }
    
    // Validate terms acceptance
    if (!formData.terms_accepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    // Validate category selection
    if (!formData.category) {
      toast.error('Please select a category for your listing');
      return;
    }
    
    // Validate price meets category minimum
    if (selectedCategory && parseFloat(formData.price_per_hour) < selectedCategory.minimum_hourly_price) {
      toast.error(`Minimum price for ${selectedCategory.name} is $${selectedCategory.minimum_hourly_price}/hour`);
      return;
    }
    
    try {
      await listingsApi.create({
        ...formData,
        price_per_hour: parseFloat(formData.price_per_hour),
      }, token);
      toast.success('Listing created! Waiting for admin approval.');
      setShowCreateForm(false);
      resetForm();
      loadListings();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      price_per_hour: '',
      location_city: '',
      location_state: '',
      location_zip: '',
      location_neighborhood: '',
      address_full: '',
      max_guests: 1,
      amenities: [],
      rules: [],
      photos: [],
      terms_accepted: false,
    });
    setNewRule('');
    setSelectedCategory(null);
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await bookingsApi.updateStatus(bookingId, status, token);
      toast.success(`Booking ${status}`);
      loadBookings();
      loadStats();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleToggleListingStatus = async (listingId) => {
    try {
      await listingsApi.toggleStatus(listingId, token);
      toast.success('Listing status updated');
      loadListings();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update listing status');
    }
  };

  const handleReactivateListing = async (listingId) => {
    try {
      await listingsApi.reactivate(listingId, { terms_accepted: true }, token);
      toast.success('Listing reactivated successfully');
      loadListings();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate listing');
    }
  };

  const handleAddPhotos = async (listingId, newPhotos) => {
    try {
      const listing = listings.find(l => l.id === listingId);
      const updatedPhotos = [...listing.photos, ...newPhotos];
      await listingsApi.update(listingId, { photos: updatedPhotos }, token);
      toast.success('Photos added successfully');
      loadListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add photos');
    }
  };

  const toggleAmenity = (amenity) => {
    if (formData.amenities.includes(amenity)) {
      setFormData(prev => ({ ...prev, amenities: prev.amenities.filter(a => a !== amenity) }));
    } else {
      setFormData(prev => ({ ...prev, amenities: [...prev.amenities, amenity] }));
    }
  };

  const addRule = () => {
    if (newRule.trim()) {
      setFormData(prev => ({ ...prev, rules: [...prev.rules, newRule.trim()] }));
      setNewRule('');
    }
  };

  const removeRule = (index) => {
    setFormData(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));
  };

  const removePhoto = (index) => {
    setFormData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  // Render different tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab stats={stats} listings={listings} bookings={bookings} onTabChange={setActiveTab} />;
      case 'listings':
        return (
          <ListingsTab 
            listings={listings} 
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            formData={formData}
            setFormData={setFormData}
            handleCreateListing={handleCreateListing}
            handleFileUpload={handleFileUpload}
            uploading={uploading}
            toggleAmenity={toggleAmenity}
            newRule={newRule}
            setNewRule={setNewRule}
            addRule={addRule}
            removeRule={removeRule}
            removePhoto={removePhoto}
            onCalendarClick={(listing) => {
              setSelectedListing(listing);
              setActiveTab('calendar');
            }}
            onToggleStatus={handleToggleListingStatus}
            onReactivate={handleReactivateListing}
            onAddPhotos={handleAddPhotos}
            token={token}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
          />
        );
      case 'bookings':
        return <BookingsTab bookings={bookings} onStatusChange={handleBookingStatus} />;
      case 'calendar':
        return <CalendarTab listings={listings} selectedListing={selectedListing} setSelectedListing={setSelectedListing} token={token} />;
      case 'messages':
        return <MessagesTab token={token} />;
      case 'earnings':
        return <EarningsTab stats={stats} token={token} user={user} />;
      case 'reviews':
        return <ReviewsTab reviews={reviews} />;
      case 'settings':
        return <SettingsTab user={user} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen bg-[#0A0A0A] border-r border-white/5 sticky top-20 h-[calc(100vh-80px)]">
          <div className="p-6">
            <h2 className="text-lg font-serif text-white mb-1">Host Dashboard</h2>
            <p className="text-zinc-500 text-sm">{user?.name}</p>
          </div>
          
          <nav className="px-3">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]'
                    : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <tab.icon size={20} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                <span>{tab.label}</span>
                {tab.id === 'bookings' && stats?.pending_bookings > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {stats.pending_bookings}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ stats, listings, bookings, onTabChange }) {
  const pendingBookings = bookings?.filter(b => b.status === 'pending') || [];

  return (
    <div>
      <h1 className="text-3xl font-serif text-white mb-8">Welcome back!</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#121212] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <House size={24} className="text-[#D4AF37]" weight="duotone" />
            <span className="text-xs text-zinc-500">Approved</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.active_listings || 0}</p>
          <p className="text-zinc-500 text-sm">Active Listings</p>
          {stats?.total_listings > stats?.active_listings && (
            <p className="text-xs text-yellow-500 mt-1">
              +{stats?.total_listings - stats?.active_listings} pending approval
            </p>
          )}
        </div>

        <div className="bg-[#121212] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <CalendarBlank size={24} className="text-blue-400" weight="duotone" />
            <span className="text-xs text-zinc-500">This Month</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.this_month_bookings || 0}</p>
          <p className="text-zinc-500 text-sm">Bookings</p>
        </div>

        <div className="bg-[#121212] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <Clock size={24} className="text-yellow-400" weight="duotone" />
            <span className="text-xs text-zinc-500">Pending</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.pending_bookings || 0}</p>
          <p className="text-zinc-500 text-sm">Awaiting Response</p>
        </div>

        <div className="bg-[#121212] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <CurrencyDollar size={24} className="text-green-400" weight="duotone" />
            <span className="text-xs text-zinc-500">Total</span>
          </div>
          <p className="text-3xl font-bold text-white">${stats?.total_earnings?.toFixed(2) || '0.00'}</p>
          <p className="text-zinc-500 text-sm">Earnings</p>
        </div>
      </div>

      {/* Pending Bookings Preview */}
      {pendingBookings.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-serif text-white">Pending Requests</h2>
            <button 
              onClick={() => onTabChange('bookings')}
              className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
            >
              View All <CaretRight size={16} />
            </button>
          </div>
          <div className="space-y-3">
            {pendingBookings.slice(0, 3).map((booking) => (
              <div key={booking.id} className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{booking.listing_title}</p>
                  <p className="text-zinc-500 text-sm">
                    {booking.guest_name} • {booking.booking_date} • {booking.start_time}-{booking.end_time}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#D4AF37] font-semibold">${booking.total_price}</span>
                  <CaretRight size={16} className="text-zinc-500" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button 
          onClick={() => onTabChange('listings')}
          className="bg-[#121212] border border-white/10 p-6 text-left hover:border-[#D4AF37]/30 transition-colors group"
        >
          <Plus size={24} className="text-[#D4AF37] mb-4" />
          <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-[#D4AF37]">Create New Listing</h3>
          <p className="text-zinc-500 text-sm">Add another space to your portfolio</p>
        </button>

        <button 
          onClick={() => onTabChange('calendar')}
          className="bg-[#121212] border border-white/10 p-6 text-left hover:border-[#D4AF37]/30 transition-colors group"
        >
          <CalendarBlank size={24} className="text-blue-400 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-[#D4AF37]">Manage Availability</h3>
          <p className="text-zinc-500 text-sm">Update your calendar and hours</p>
        </button>
      </div>
    </div>
  );
}

// Listings Tab Component
function ListingsTab({ 
  listings, 
  showCreateForm, 
  setShowCreateForm, 
  formData, 
  setFormData,
  handleCreateListing,
  handleFileUpload,
  uploading,
  toggleAmenity,
  newRule,
  setNewRule,
  addRule,
  removeRule,
  removePhoto,
  onCalendarClick,
  onToggleStatus,
  onReactivate,
  onAddPhotos,
  token,
  categories,
  selectedCategory,
  setSelectedCategory
}) {
  const navigate = useNavigate();
  const [showReactivateModal, setShowReactivateModal] = useState(null);
  const [showAddPhotosModal, setShowAddPhotosModal] = useState(null);
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const handlePhotoUploadForEdit = async (e) => {
    const files = Array.from(e.target.files);
    setUploadingPhotos(true);
    
    try {
      const uploadPromises = files.map(file => uploadApi.upload(file, token));
      const results = await Promise.all(uploadPromises);
      const paths = results.map(r => r.data.path);
      setNewPhotos(prev => [...prev, ...paths]);
      toast.success('Photos uploaded');
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleSaveNewPhotos = async (listingId) => {
    if (newPhotos.length > 0) {
      await onAddPhotos(listingId, newPhotos);
    }
    setShowAddPhotosModal(null);
    setNewPhotos([]);
  };

  const getStatusBadge = (listing) => {
    if (!listing.is_active) {
      return <span className="text-xs px-2 py-1 bg-zinc-600/90 text-white rounded">Paused</span>;
    }
    if (listing.is_approved) {
      return <span className="text-xs px-2 py-1 bg-green-600/90 text-white rounded">Active</span>;
    }
    return <span className="text-xs px-2 py-1 bg-yellow-600/90 text-white rounded">Pending</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-serif text-white">My Listings</h1>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          data-testid="create-listing-button"
          className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
        >
          <Plus size={20} className="mr-2" />
          {showCreateForm ? 'Cancel' : 'Create Listing'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-[#121212] border border-white/10 p-8 mb-8">
          <h2 className="text-2xl font-serif text-white mb-6">Create New Listing</h2>
          <form onSubmit={handleCreateListing} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="title" className="text-white mb-2 block">Title *</Label>
                <Input
                  data-testid="listing-title-input"
                  id="title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="Cozy Reading Room with City Views"
                />
              </div>

              <div>
                <Label htmlFor="category" className="text-white mb-2 block">Category *</Label>
                <select
                  data-testid="listing-category-select"
                  id="category"
                  required
                  value={formData.category}
                  onChange={(e) => {
                    const cat = categories.find(c => c.slug === e.target.value);
                    setSelectedCategory(cat || null);
                    setFormData({ 
                      ...formData, 
                      category: e.target.value,
                      // Auto-set minimum price when category changes
                      price_per_hour: cat && (!formData.price_per_hour || parseFloat(formData.price_per_hour) < cat.minimum_hourly_price) 
                        ? cat.minimum_hourly_price.toString() 
                        : formData.price_per_hour
                    });
                  }}
                  className="w-full bg-[#121212] border border-white/20 text-white py-3 px-4 rounded-md"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.slug} value={cat.slug}>
                      {cat.name} (min ${cat.minimum_hourly_price}/hr)
                    </option>
                  ))}
                </select>
                {selectedCategory && (
                  <p className="text-[#D4AF37] text-sm mt-2">
                    Minimum price for {selectedCategory.name} is ${selectedCategory.minimum_hourly_price}/hour
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="city" className="text-white mb-2 block">City *</Label>
                <Input
                  data-testid="listing-city-input"
                  id="city"
                  required
                  value={formData.location_city}
                  onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="state" className="text-white mb-2 block">State * <span className="text-zinc-500 text-xs">(USA only)</span></Label>
                <select
                  data-testid="listing-state-input"
                  id="state"
                  required
                  value={formData.location_state}
                  onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                  className="w-full bg-transparent border border-white/20 text-white p-2 rounded-md"
                >
                  <option value="" className="bg-[#121212]">Select a state...</option>
                  <option value="AL" className="bg-[#121212]">Alabama</option>
                  <option value="AK" className="bg-[#121212]">Alaska</option>
                  <option value="AZ" className="bg-[#121212]">Arizona</option>
                  <option value="AR" className="bg-[#121212]">Arkansas</option>
                  <option value="CA" className="bg-[#121212]">California</option>
                  <option value="CO" className="bg-[#121212]">Colorado</option>
                  <option value="CT" className="bg-[#121212]">Connecticut</option>
                  <option value="DE" className="bg-[#121212]">Delaware</option>
                  <option value="DC" className="bg-[#121212]">District of Columbia</option>
                  <option value="FL" className="bg-[#121212]">Florida</option>
                  <option value="GA" className="bg-[#121212]">Georgia</option>
                  <option value="HI" className="bg-[#121212]">Hawaii</option>
                  <option value="ID" className="bg-[#121212]">Idaho</option>
                  <option value="IL" className="bg-[#121212]">Illinois</option>
                  <option value="IN" className="bg-[#121212]">Indiana</option>
                  <option value="IA" className="bg-[#121212]">Iowa</option>
                  <option value="KS" className="bg-[#121212]">Kansas</option>
                  <option value="KY" className="bg-[#121212]">Kentucky</option>
                  <option value="LA" className="bg-[#121212]">Louisiana</option>
                  <option value="ME" className="bg-[#121212]">Maine</option>
                  <option value="MD" className="bg-[#121212]">Maryland</option>
                  <option value="MA" className="bg-[#121212]">Massachusetts</option>
                  <option value="MI" className="bg-[#121212]">Michigan</option>
                  <option value="MN" className="bg-[#121212]">Minnesota</option>
                  <option value="MS" className="bg-[#121212]">Mississippi</option>
                  <option value="MO" className="bg-[#121212]">Missouri</option>
                  <option value="MT" className="bg-[#121212]">Montana</option>
                  <option value="NE" className="bg-[#121212]">Nebraska</option>
                  <option value="NV" className="bg-[#121212]">Nevada</option>
                  <option value="NH" className="bg-[#121212]">New Hampshire</option>
                  <option value="NJ" className="bg-[#121212]">New Jersey</option>
                  <option value="NM" className="bg-[#121212]">New Mexico</option>
                  <option value="NY" className="bg-[#121212]">New York</option>
                  <option value="NC" className="bg-[#121212]">North Carolina</option>
                  <option value="ND" className="bg-[#121212]">North Dakota</option>
                  <option value="OH" className="bg-[#121212]">Ohio</option>
                  <option value="OK" className="bg-[#121212]">Oklahoma</option>
                  <option value="OR" className="bg-[#121212]">Oregon</option>
                  <option value="PA" className="bg-[#121212]">Pennsylvania</option>
                  <option value="PR" className="bg-[#121212]">Puerto Rico</option>
                  <option value="RI" className="bg-[#121212]">Rhode Island</option>
                  <option value="SC" className="bg-[#121212]">South Carolina</option>
                  <option value="SD" className="bg-[#121212]">South Dakota</option>
                  <option value="TN" className="bg-[#121212]">Tennessee</option>
                  <option value="TX" className="bg-[#121212]">Texas</option>
                  <option value="UT" className="bg-[#121212]">Utah</option>
                  <option value="VT" className="bg-[#121212]">Vermont</option>
                  <option value="VA" className="bg-[#121212]">Virginia</option>
                  <option value="WA" className="bg-[#121212]">Washington</option>
                  <option value="WV" className="bg-[#121212]">West Virginia</option>
                  <option value="WI" className="bg-[#121212]">Wisconsin</option>
                  <option value="WY" className="bg-[#121212]">Wyoming</option>
                </select>
                <p className="text-zinc-500 text-xs mt-1">100Nook currently operates in the USA only</p>
              </div>

              <div>
                <Label htmlFor="neighborhood" className="text-white mb-2 block">Neighborhood</Label>
                <Input
                  data-testid="listing-neighborhood-input"
                  id="neighborhood"
                  value={formData.location_neighborhood}
                  onChange={(e) => setFormData({ ...formData, location_neighborhood: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="e.g., Downtown, West Side"
                />
              </div>

              <div>
                <Label htmlFor="zip" className="text-white mb-2 block">Zip Code *</Label>
                <Input
                  data-testid="listing-zip-input"
                  id="zip"
                  required
                  value={formData.location_zip}
                  onChange={(e) => setFormData({ ...formData, location_zip: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="address" className="text-white mb-2 block">Full Address *</Label>
                <Input
                  data-testid="listing-address-input"
                  id="address"
                  required
                  value={formData.address_full}
                  onChange={(e) => setFormData({ ...formData, address_full: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="123 Main St, Apt 4B"
                />
                <p className="text-zinc-500 text-xs mt-1">Only shown to confirmed guests</p>
              </div>

              <div>
                <Label htmlFor="price" className="text-white mb-2 block">Price per Hour ($) *</Label>
                <Input
                  data-testid="listing-price-input"
                  id="price"
                  type="number"
                  required
                  min={selectedCategory?.minimum_hourly_price || 1}
                  step="0.01"
                  value={formData.price_per_hour}
                  onChange={(e) => setFormData({ ...formData, price_per_hour: e.target.value })}
                  className={`bg-transparent border-white/20 text-white ${
                    selectedCategory && formData.price_per_hour && parseFloat(formData.price_per_hour) < selectedCategory.minimum_hourly_price
                      ? 'border-red-500'
                      : ''
                  }`}
                />
                {selectedCategory && formData.price_per_hour && parseFloat(formData.price_per_hour) < selectedCategory.minimum_hourly_price && (
                  <p className="text-red-400 text-sm mt-1">
                    Price must be at least ${selectedCategory.minimum_hourly_price}/hour for this category
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="guests" className="text-white mb-2 block">Max Guests *</Label>
                <Input
                  data-testid="listing-maxguests-input"
                  id="guests"
                  type="number"
                  required
                  min="1"
                  value={formData.max_guests}
                  onChange={(e) => setFormData({ ...formData, max_guests: parseInt(e.target.value) })}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="text-white mb-2 block">Description *</Label>
              <Textarea
                data-testid="listing-description-input"
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-transparent border-white/20 text-white"
                rows={5}
                placeholder="Describe your space in detail..."
              />
            </div>

            <div>
              <Label className="text-white mb-3 block">Amenities</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {AMENITIES.map((amenity) => (
                  <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                      className="w-4 h-4 rounded border-white/20 bg-transparent"
                    />
                    <span className="text-zinc-400 text-sm">{amenity}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">House Rules</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  data-testid="listing-rule-input"
                  value={newRule}
                  onChange={(e) => setNewRule(e.target.value)}
                  placeholder="Add a rule..."
                  className="bg-transparent border-white/20 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRule())}
                />
                <Button type="button" onClick={addRule} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black">
                  Add
                </Button>
              </div>
              {formData.rules.length > 0 && (
                <div className="space-y-2">
                  {formData.rules.map((rule, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 p-3 rounded">
                      <span className="text-zinc-400">{rule}</span>
                      <button type="button" onClick={() => removeRule(idx)} className="text-red-500 hover:text-red-400">
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-white mb-3 block">Photos <span className="text-red-400">*</span></Label>
              <div className={`border-2 border-dashed p-6 text-center rounded-lg ${formData.photos.length === 0 ? 'border-red-500/30' : 'border-white/20'}`}>
                <input
                  data-testid="listing-photo-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Upload size={48} className="mx-auto text-zinc-500 mb-3" weight="duotone" />
                  <p className="text-zinc-400">
                    {uploading ? 'Uploading...' : 'Click to upload photos'}
                  </p>
                  <p className="text-zinc-600 text-sm mt-1">At least one photo is required</p>
                </label>
              </div>
              {formData.photos.length > 0 && (
                <div className="grid grid-cols-4 gap-3 mt-4">
                  {formData.photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img src={`${API_URL}/api/files/${photo}`} alt="Preview" className="w-full h-24 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="bg-[#0A0A0A] border border-white/10 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">Terms & Conditions</h4>
              <div className="max-h-48 overflow-y-auto mb-4 p-4 bg-[#121212] rounded text-sm text-zinc-400 space-y-3">
                <p><strong className="text-white">Property Authorization:</strong> By listing this space, I confirm that I am either the legal owner of this property or have explicit written authorization from the property owner to list it on 100Nook for guest use.</p>
                <p><strong className="text-white">Accuracy of Information:</strong> I certify that all information provided about this listing, including photos, descriptions, amenities, and pricing, is accurate and truthful to the best of my knowledge.</p>
                <p><strong className="text-white">Compliance:</strong> I agree to comply with all applicable local laws, regulations, zoning requirements, and homeowner association rules regarding short-term or hourly rentals of private spaces.</p>
                <p><strong className="text-white">Payouts:</strong> Host payouts are processed after the booking is completed and a hold period has passed to allow for dispute resolution. Payout timing may vary and 100Nook reserves the right to withhold payouts pending investigation of any disputes or policy violations.</p>
                <p><strong className="text-white">Insurance & Liability:</strong> I understand that I am responsible for maintaining appropriate insurance coverage for my property and any activities that occur on the premises. 100Nook is not liable for any damages, injuries, or losses that may occur during guest visits.</p>
                <p><strong className="text-white">Platform Liability Waiver:</strong> I acknowledge and agree that 100Nook acts solely as a marketplace platform connecting hosts and guests. 100Nook is not responsible for verifying the ownership or authorization status of any listing, and I release 100Nook from any liability arising from disputes related to property ownership, authorization, or fraudulent listings.</p>
                <p><strong className="text-white">Indemnification:</strong> I agree to indemnify and hold harmless 100Nook, its affiliates, officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from my listing, including but not limited to claims of unauthorized property listing, guest injuries, property damage, or violations of applicable laws.</p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="terms-checkbox"
                  checked={formData.terms_accepted}
                  onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                  className="mt-1 w-5 h-5 rounded border-white/30 bg-transparent"
                />
                <span className="text-zinc-300 text-sm">
                  I have read and agree to the above terms and conditions. I confirm that I am authorized to list this property and accept full responsibility for my listing.
                  <span className="text-red-400"> *</span>
                </span>
              </label>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                data-testid="submit-listing-button"
                type="submit"
                disabled={!formData.terms_accepted || formData.photos.length === 0}
                className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold px-8 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Listing
              </Button>
              <Button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Listings Grid */}
      {listings.length === 0 ? (
        <div className="text-center py-16 bg-[#121212] border border-white/10">
          <House size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <h3 className="text-xl text-white mb-2">No listings yet</h3>
          <p className="text-zinc-500 mb-6">Create your first listing to start hosting</p>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
          >
            <Plus size={20} className="mr-2" />
            Create Your First Listing
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((listing) => (
            <div key={listing.id} className="bg-[#121212] border border-white/10 overflow-hidden group">
              <div className="h-48 relative">
                <img
                  src={getPhotoUrl(listing.photos?.[0]) || FALLBACK_LISTING_IMAGE}
                  alt={listing.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = FALLBACK_LISTING_IMAGE;
                  }}
                />
                <div className="absolute top-3 right-3">
                  {getStatusBadge(listing)}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-1 truncate">{listing.title}</h3>
                <p className="text-zinc-500 text-sm mb-3">{listing.location_city}</p>
                <p className="text-[#D4AF37] font-semibold mb-4">${listing.price_per_hour}/hour</p>
                
                {/* Action Buttons */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/listing/${listing.id}`)}
                      className="flex-1 bg-transparent border border-white/20 text-white hover:bg-white/5"
                      size="sm"
                    >
                      <Eye size={16} className="mr-1" />
                      View
                    </Button>
                    <Button
                      onClick={() => onCalendarClick(listing)}
                      className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                      size="sm"
                    >
                      <CalendarBlank size={16} className="mr-1" />
                      Availability
                    </Button>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowAddPhotosModal(listing.id)}
                      className="flex-1 bg-transparent border border-white/20 text-white hover:bg-white/5"
                      size="sm"
                    >
                      <Upload size={16} className="mr-1" />
                      Add Photos
                    </Button>
                    
                    {listing.is_active ? (
                      <Button
                        onClick={() => onToggleStatus(listing.id)}
                        className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white"
                        size="sm"
                      >
                        Pause
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setShowReactivateModal(listing.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        size="sm"
                      >
                        Reactivate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reactivate Modal */}
      {showReactivateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] border border-white/10 p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-serif text-white mb-4">Reactivate Listing</h3>
            <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-4 max-h-48 overflow-y-auto text-sm text-zinc-400 space-y-2">
              <p><strong className="text-white">Property Authorization:</strong> I confirm that I am either the legal owner of this property or have explicit authorization to list it.</p>
              <p><strong className="text-white">Platform Liability Waiver:</strong> I acknowledge that 100Nook is not liable for any damages, injuries, or losses during guest visits.</p>
              <p><strong className="text-white">Indemnification:</strong> I agree to indemnify 100Nook from any claims arising from my listing.</p>
            </div>
            <p className="text-zinc-300 mb-6">
              By reactivating, you confirm you still have authorization to list this property and accept the terms above.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  onReactivate(showReactivateModal);
                  setShowReactivateModal(null);
                }}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
              >
                Accept & Reactivate
              </Button>
              <Button
                onClick={() => setShowReactivateModal(null)}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Photos Modal */}
      {showAddPhotosModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] border border-white/10 p-6 max-w-lg w-full">
            <h3 className="text-xl font-serif text-white mb-4">Add More Photos</h3>
            <p className="text-zinc-400 text-sm mb-4">
              You can add new photos to your listing. Existing photos cannot be removed.
            </p>
            
            <div className="border-2 border-dashed border-white/20 p-6 text-center rounded-lg mb-4">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUploadForEdit}
                className="hidden"
                id="add-photos-upload"
              />
              <label htmlFor="add-photos-upload" className="cursor-pointer">
                <Upload size={36} className="mx-auto text-zinc-500 mb-2" weight="duotone" />
                <p className="text-zinc-400">
                  {uploadingPhotos ? 'Uploading...' : 'Click to upload photos'}
                </p>
              </label>
            </div>
            
            {newPhotos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-4">
                {newPhotos.map((photo, idx) => (
                  <div key={idx} className="relative">
                    <img src={`${API_URL}/api/files/${photo}`} alt="Preview" className="w-full h-20 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => setNewPhotos(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                onClick={() => handleSaveNewPhotos(showAddPhotosModal)}
                disabled={newPhotos.length === 0}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold disabled:opacity-50"
              >
                Save Photos
              </Button>
              <Button
                onClick={() => {
                  setShowAddPhotosModal(null);
                  setNewPhotos([]);
                }}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Bookings Tab Component
function BookingsTab({ bookings, onStatusChange }) {
  const [filter, setFilter] = useState('all');

  const filteredBookings = bookings.filter(b => {
    if (filter === 'all') return true;
    return b.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
      case 'approved': return 'bg-green-500/10 text-green-400 border-green-500/30';
      case 'confirmed': return 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30';
      case 'completed': return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      case 'rejected': return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'cancelled': return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30';
      default: return 'bg-zinc-500/10 text-zinc-400';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-serif text-white">Booking Requests</h1>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'confirmed', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-full text-sm capitalize transition-all ${
                filter === status
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-white/5 text-zinc-400 hover:bg-white/10'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="text-center py-16 bg-[#121212] border border-white/10">
          <CalendarBlank size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <h3 className="text-xl text-white mb-2">No bookings found</h3>
          <p className="text-zinc-500">
            {filter === 'all' ? 'You have no booking requests yet' : `No ${filter} bookings`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-white">{booking.listing_title}</h3>
                    <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-500">Guest</p>
                      <p className="text-white">{booking.guest_name}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Date</p>
                      <p className="text-white">{booking.booking_date}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Time</p>
                      <p className="text-white">{booking.start_time} - {booking.end_time}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Total</p>
                      <p className="text-[#D4AF37] font-semibold">${booking.total_price}</p>
                    </div>
                  </div>
                  {booking.message && (
                    <p className="text-zinc-400 mt-4 italic bg-white/5 p-3 rounded">"{booking.message}"</p>
                  )}
                </div>
                
                {booking.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      onClick={() => onStatusChange(booking.id, 'approved')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      <CheckCircle size={18} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => onStatusChange(booking.id, 'rejected')}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      size="sm"
                    >
                      <XCircle size={18} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Calendar Tab Component
function CalendarTab({ listings, selectedListing, setSelectedListing, token }) {
  return (
    <div>
      <h1 className="text-3xl font-serif text-white mb-8">Availability Calendar</h1>
      
      {listings.length === 0 ? (
        <div className="text-center py-16 bg-[#121212] border border-white/10">
          <CalendarBlank size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <h3 className="text-xl text-white mb-2">No listings to manage</h3>
          <p className="text-zinc-500">Create a listing first to manage availability</p>
        </div>
      ) : (
        <div>
          {/* Listing Selector */}
          <div className="mb-6">
            <Label className="text-white mb-2 block">Select Listing</Label>
            <select
              value={selectedListing?.id || ''}
              onChange={(e) => {
                const listing = listings.find(l => l.id === e.target.value);
                setSelectedListing(listing);
              }}
              className="w-full max-w-md bg-[#121212] border border-white/20 text-white py-3 px-4 rounded-md"
            >
              <option value="">Choose a listing...</option>
              {listings.map((listing) => (
                <option key={listing.id} value={listing.id}>{listing.title}</option>
              ))}
            </select>
          </div>

          {selectedListing ? (
            <AvailabilityCalendar listing={selectedListing} token={token} />
          ) : (
            <div className="text-center py-16 bg-[#121212] border border-white/10">
              <CalendarBlank size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
              <p className="text-zinc-500">Select a listing to manage its availability</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Messages Tab Component
function MessagesTab({ token }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/host/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/messages/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        // Mark messages as read
        await fetch(`${API_URL}/api/messages/${bookingId}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        loadConversations(); // Refresh unread counts
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: selectedConversation.booking_id,
          content: newMessage
        })
      });
      
      if (response.ok) {
        setNewMessage('');
        loadMessages(selectedConversation.booking_id);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    loadMessages(conv.booking_id);
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <p className="text-zinc-400">Loading conversations...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-serif text-white mb-8">Messages</h1>
      
      {conversations.length === 0 ? (
        <div className="text-center py-16 bg-[#121212] border border-white/10">
          <ChatsCircle size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <h3 className="text-xl text-white mb-2">No conversations yet</h3>
          <p className="text-zinc-500">Messages from guests will appear here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversations List */}
          <div className="lg:col-span-1 bg-[#121212] border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Conversations</h3>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {conversations.map((conv) => (
                <button
                  key={conv.booking_id}
                  onClick={() => selectConversation(conv)}
                  data-testid={`host-conversation-${conv.booking_id}`}
                  className={`w-full p-4 text-left border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${
                    selectedConversation?.booking_id === conv.booking_id ? 'bg-[#D4AF37]/10 border-l-2 border-l-[#D4AF37]' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <p className="font-semibold text-white truncate">{conv.guest_name}</p>
                    <div className="flex items-center gap-2">
                      {conv.unread_count > 0 && (
                        <span className="bg-[#D4AF37] text-black text-xs px-2 py-0.5 rounded-full font-semibold">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-zinc-500 truncate">{conv.listing_title}</p>
                  {conv.last_message && (
                    <p className="text-xs text-zinc-600 truncate mt-1">
                      {conv.last_message.content}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Messages Panel */}
          <div className="lg:col-span-2 bg-[#121212] border border-white/10 flex flex-col h-[600px]">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white">{selectedConversation.guest_name}</h3>
                  <p className="text-sm text-zinc-500">{selectedConversation.listing_title}</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">No messages yet. Start the conversation!</p>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id} className="flex flex-col">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-semibold text-zinc-400">{msg.sender_name}</p>
                          <p className="text-xs text-zinc-600">
                            {new Date(msg.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-[#0A0A0A] border border-white/10 p-3 rounded">
                          <p className="text-zinc-300">{msg.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="bg-transparent border-white/20 text-white flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      data-testid="host-message-input"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                      data-testid="host-send-message-btn"
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <ChatsCircle size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
                  <p className="text-zinc-500">Select a conversation to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Earnings Tab Component - Full Implementation with Stripe Connect
function EarningsTab({ stats, token, user }) {
  const [earnings, setEarnings] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    loadEarningsData();
  }, []);

  const loadEarningsData = async () => {
    setLoading(true);
    try {
      const [earningsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/host/earnings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/host/connect/status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (earningsRes.ok) {
        const data = await earningsRes.json();
        setEarnings(data);
      }
      if (statusRes.ok) {
        const data = await statusRes.json();
        setConnectStatus(data);
      }
    } catch (error) {
      console.error('Failed to load earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectBank = async () => {
    setConnectLoading(true);
    try {
      // First create account if not exists
      if (!connectStatus?.connected) {
        const createRes = await fetch(`${API_URL}/api/host/connect/create-account`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ country: 'US' })
        });
        
        if (!createRes.ok) {
          const err = await createRes.json();
          throw new Error(err.detail || 'Failed to create account');
        }
      }
      
      // Get onboarding link
      const linkRes = await fetch(`${API_URL}/api/host/connect/onboarding-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (linkRes.ok) {
        const data = await linkRes.json();
        window.location.href = data.onboarding_url;
      } else {
        const err = await linkRes.json();
        throw new Error(err.detail || 'Failed to get onboarding link');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!earnings?.summary?.available || earnings.summary.available < 50) {
      toast.error('Minimum payout amount is $50.00');
      return;
    }
    
    setPayoutLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/host/connect/request-payout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({})
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Payout requested successfully!');
        loadEarningsData();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Payout failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleViewStripeDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/host/connect/dashboard-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        window.open(data.dashboard_url, '_blank');
      } else {
        toast.error('Failed to open Stripe dashboard');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = earnings?.summary || { pending: 0, held: 0, available: 0, paid_total: 0, pending_disputes: 0 };

  return (
    <div>
      <h1 className="text-3xl font-serif text-white mb-8">Earnings & Payouts</h1>
      
      {/* Stripe Connect Status Banner */}
      {!connectStatus?.connected || !connectStatus?.payouts_enabled ? (
        <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Connect Your Bank Account</h3>
              <p className="text-zinc-400 text-sm">
                Set up direct deposits to receive your earnings. Payouts are processed securely via Stripe.
              </p>
            </div>
            <Button
              onClick={handleConnectBank}
              disabled={connectLoading}
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black whitespace-nowrap"
            >
              {connectLoading ? 'Loading...' : connectStatus?.connected ? 'Complete Setup' : 'Connect Bank Account'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-green-900/20 border border-green-500/30 p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-green-500" weight="fill" />
            <span className="text-green-400">Bank account connected - Ready for payouts</span>
          </div>
          <Button
            onClick={handleViewStripeDashboard}
            variant="outline"
            className="border-green-500/30 text-green-400 hover:bg-green-500/10"
          >
            View Stripe Dashboard
          </Button>
        </div>
      )}
      
      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#121212] border border-white/10 p-6">
          <p className="text-zinc-500 text-sm mb-2">Pending</p>
          <p className="text-2xl font-bold text-zinc-400">${summary.pending.toFixed(2)}</p>
          <p className="text-xs text-zinc-600 mt-1">Bookings not yet completed</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-6">
          <p className="text-zinc-500 text-sm mb-2">In Hold</p>
          <p className="text-2xl font-bold text-yellow-500">${summary.held.toFixed(2)}</p>
          <p className="text-xs text-zinc-600 mt-1">7-day hold period</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-6">
          <p className="text-zinc-500 text-sm mb-2">Available</p>
          <p className="text-2xl font-bold text-[#D4AF37]">${summary.available.toFixed(2)}</p>
          <p className="text-xs text-zinc-600 mt-1">Ready for payout</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-6">
          <p className="text-zinc-500 text-sm mb-2">Total Paid Out</p>
          <p className="text-2xl font-bold text-green-500">${summary.paid_total.toFixed(2)}</p>
          <p className="text-xs text-zinc-600 mt-1">Lifetime payouts</p>
        </div>
      </div>

      {/* Request Payout Section */}
      {connectStatus?.payouts_enabled && summary.available >= 50 && (
        <div className="bg-[#121212] border border-[#D4AF37]/30 p-6 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Request Payout</h3>
              <p className="text-zinc-400 text-sm">
                You have ${summary.available.toFixed(2)} available for payout (minimum $50.00)
              </p>
            </div>
            <Button
              onClick={handleRequestPayout}
              disabled={payoutLoading}
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
            >
              {payoutLoading ? 'Processing...' : `Payout $${summary.available.toFixed(2)}`}
            </Button>
          </div>
        </div>
      )}

      {/* Disputes Warning */}
      {summary.pending_disputes > 0 && (
        <div className="bg-red-900/20 border border-red-500/30 p-4 mb-8 flex items-center gap-3">
          <XCircle size={24} className="text-red-500" weight="fill" />
          <span className="text-red-400">
            You have {summary.pending_disputes} pending dispute(s). Affected payouts are on hold until resolved.
          </span>
        </div>
      )}

      {/* Booking Earnings Breakdown */}
      <div className="bg-[#121212] border border-white/10">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Booking Earnings</h3>
        </div>
        
        {earnings?.bookings?.length > 0 ? (
          <div className="divide-y divide-white/5">
            {earnings.bookings.map((booking) => (
              <div key={booking.booking_id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{booking.listing_title}</p>
                  <p className="text-zinc-500 text-sm">
                    {booking.booking_date} • ${booking.amount.toFixed(2)}
                  </p>
                  {booking.payout_eligible_date && booking.payout_status !== 'paid' && booking.payout_status !== 'available' && (
                    <p className="text-xs text-zinc-600">
                      Available for payout: {booking.payout_eligible_date}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    booking.payout_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                    booking.payout_status === 'available' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                    booking.payout_status === 'held' ? 'bg-yellow-500/20 text-yellow-400' :
                    booking.payout_status === 'disputed' ? 'bg-red-500/20 text-red-400' :
                    'bg-zinc-500/20 text-zinc-400'
                  }`}>
                    {booking.payout_status === 'paid' ? 'Paid' :
                     booking.payout_status === 'available' ? 'Available' :
                     booking.payout_status === 'held' ? 'In Hold' :
                     booking.payout_status === 'disputed' ? 'Disputed' :
                     'Pending'}
                  </span>
                  {booking.has_dispute && (
                    <p className="text-xs text-red-400 mt-1">Dispute filed</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <CurrencyDollar size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
            <p className="text-zinc-500">No earnings yet. Complete bookings to start earning!</p>
          </div>
        )}
      </div>

      {/* Payout History */}
      {earnings?.payouts?.length > 0 && (
        <div className="bg-[#121212] border border-white/10 mt-8">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white">Payout History</h3>
          </div>
          <div className="divide-y divide-white/5">
            {earnings.payouts.map((payout) => (
              <div key={payout.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">${payout.amount.toFixed(2)}</p>
                  <p className="text-zinc-500 text-sm">
                    {new Date(payout.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="px-3 py-1 text-xs rounded-full bg-green-500/20 text-green-400">
                  Completed
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-8 p-6 bg-[#0a0a0a] border border-white/5 rounded-lg">
        <h4 className="text-white font-semibold mb-3">How Payouts Work</h4>
        <ul className="text-zinc-400 text-sm space-y-2">
          <li>• Earnings are held for 7 days after each booking to allow for dispute resolution</li>
          <li>• Minimum payout amount is $50.00</li>
          <li>• Payouts are processed directly to your connected bank account via Stripe</li>
          <li>• Guests can raise disputes within 7 days of their booking date</li>
        </ul>
      </div>
    </div>
  );
}

// Reviews Tab Component
function ReviewsTab({ reviews }) {
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div>
      <h1 className="text-3xl font-serif text-white mb-8">Reviews</h1>
      
      {/* Summary */}
      <div className="bg-[#121212] border border-white/10 p-6 mb-8">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-4xl font-bold text-[#D4AF37]">{averageRating}</p>
            <div className="flex items-center gap-1 justify-center my-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star} 
                  size={20} 
                  weight={star <= Math.round(averageRating) ? 'fill' : 'regular'}
                  className={star <= Math.round(averageRating) ? 'text-[#D4AF37]' : 'text-zinc-600'}
                />
              ))}
            </div>
            <p className="text-zinc-500 text-sm">{reviews.length} reviews</p>
          </div>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-16 bg-[#121212] border border-white/10">
          <Star size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <h3 className="text-xl text-white mb-2">No reviews yet</h3>
          <p className="text-zinc-500">Reviews will appear here after guests complete their bookings</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white font-semibold">{review.reviewer_name}</p>
                  <p className="text-zinc-500 text-sm">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      size={16} 
                      weight={star <= review.rating ? 'fill' : 'regular'}
                      className={star <= review.rating ? 'text-[#D4AF37]' : 'text-zinc-600'}
                    />
                  ))}
                </div>
              </div>
              <p className="text-zinc-300">{review.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Settings Tab Component
function SettingsTab({ user }) {
  return (
    <div>
      <h1 className="text-3xl font-serif text-white mb-8">Host Settings</h1>
      
      <div className="max-w-2xl space-y-6">
        {/* Profile Info */}
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Host Profile</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-500 text-sm">Name</Label>
              <p className="text-white">{user?.name}</p>
            </div>
            <div>
              <Label className="text-zinc-500 text-sm">Bio</Label>
              <p className="text-white">{user?.host_bio || 'No bio added'}</p>
            </div>
            <div>
              <Label className="text-zinc-500 text-sm">Languages</Label>
              <p className="text-white">{user?.host_languages?.join(', ') || 'English'}</p>
            </div>
            <div>
              <Label className="text-zinc-500 text-sm">Response Time</Label>
              <p className="text-white capitalize">{user?.host_response_time?.replace('_', ' ') || 'Within an hour'}</p>
            </div>
          </div>
        </div>

        {/* Verification Status */}
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Verification Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Email</span>
              <span className={user?.email_verified ? 'text-green-400' : 'text-yellow-400'}>
                {user?.email_verified ? 'Verified' : 'Pending'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Phone</span>
              <span className={user?.phone_verified ? 'text-green-400' : 'text-yellow-400'}>
                {user?.phone_verified ? 'Verified' : 'Pending'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">ID</span>
              <span className={user?.id_verified ? 'text-green-400' : 'text-yellow-400'}>
                {user?.id_verified ? 'Verified' : 'Pending'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
